"use client";

import { useRef } from "react";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./alert-dialog";
import { Button } from "./button";

export interface ConfirmDeleteDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: ReactNode;
	description: ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	/** Blocks both buttons and shows a spinner while the mutation is in flight. */
	pending?: boolean;
	onConfirm: () => void;
}

const DEFAULT_CONFIRM_LABEL = "Delete";
const DEFAULT_CANCEL_LABEL = "Cancel";

/**
 * Destructive confirmation. The confirm button does not close the dialog on
 * its own — the caller closes it once its mutation succeeds, so a failure
 * leaves the dialog open with the error visible.
 */
export function ConfirmDeleteDialog({
	open,
	onOpenChange,
	title,
	description,
	confirmLabel = DEFAULT_CONFIRM_LABEL,
	cancelLabel = DEFAULT_CANCEL_LABEL,
	pending = false,
	onConfirm,
}: ConfirmDeleteDialogProps) {
	// Callers null their target on close, but Radix keeps the dialog mounted
	// through the exit animation — retain the last open content so it never
	// flashes "Delete undefined?" while animating out.
	const lastContent = useRef({ description, title });
	if (open) lastContent.current = { description, title };
	const shown = open ? { description, title } : lastContent.current;
	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{shown.title}</AlertDialogTitle>
					<AlertDialogDescription>{shown.description}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
					<Button variant="destructive" disabled={pending} onClick={onConfirm}>
						{pending ? <Loader2 className="size-4 animate-spin" /> : confirmLabel}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
